import {
    CombinedTimeSeriesOverview,
    StatDisplay,
} from 'components/TimeSeriesOverview';
import { forwardRef, memo } from 'react';
import Chart from './Charts/MultiPredictionTimeSeriesChart';
import DriversTooltipBase from './Charts/MultiPredictionTimeSeriesChart/components/DriversOfGrowthTooltip';
import { INSTAGRAM_METADATA_BY_KEY } from 'utils/metrics';
import InstagramTimeSeriesNoDataMessage from 'components/DataVis/InstagramTimeSeriesNoDataMessage';
import { Wrapper } from 'components/DataVis/TimeSeriesChart';
import { timeseries as copy } from '@/copy';
import dayjs from 'dayjs';
import { getXAxisTickFns } from './Charts/TikTokSearchTimeSeries/utils';
import isEqual from 'react-fast-compare';
import styled from 'styled-components';
import weekOfYear from 'dayjs/plugin/weekOfYear';

dayjs.extend(weekOfYear);

const formatAndParseTime = (v) => {
    const s = dayjs(v, 'YYYY-MM-DD').format('MMM D, YYYY');
    return s;
};

const InstagramTooltip = forwardRef(
    (
        {
            formatAndParseTime: formatAndParseTimeProp = formatAndParseTime,
            metricLabel = 'likes',
            sourceName = 'Instagram',
            dataSource = 'instagram',
            ...props
        },
        ref,
    ) => (
        <DriversTooltipBase
            {...props}
            formatAndParseTime={formatAndParseTimeProp}
            metricLabel={metricLabel}
            sourceName={sourceName}
            dataSource={dataSource}
            ref={ref}
        />
    ),
);

const InstagramTimeSeriesWrapper = styled(Wrapper)`
    grid-template-columns: 1fr 72.1%;
    background: transparent;
`;

const defaultMetricsList = [
    INSTAGRAM_METADATA_BY_KEY.non_cum_YoY_rolling,
    INSTAGRAM_METADATA_BY_KEY.non_cum_QoQ_rolling,
    INSTAGRAM_METADATA_BY_KEY.non_cum_MoM_rolling,
    INSTAGRAM_METADATA_BY_KEY.non_cum_WoW,
];

const InstagramTimeSeries = memo(
    forwardRef(
        (
            {
                data,
                week,
                year,
                isLoading,
                noDataMessage = <InstagramTimeSeriesNoDataMessage />,
                Wrapper = InstagramTimeSeriesWrapper,
                Tooltip = InstagramTooltip,
                metricsList = defaultMetricsList,
                metrics,
                defaultMetric = INSTAGRAM_METADATA_BY_KEY.non_cum_YoY_rolling,
                chartHeight = 250,
                showStatDisplay = false,
                showDataSourceBar = true,
                customCss,
                country,
                category,
                brand,
            },
            ref,
        ) => {
            const { xAxisTickFormat, xAxisTickValues } = getXAxisTickFns(data);
            return (
                <div css="margin-bottom: 12px;" ref={ref}>
                    <Chart
                        chartHeight={chartHeight}
                        data={data}
                        isLoading={isLoading}
                        showPlaceholder={!data || data.length < 1}
                        placeholderMessage={
                            isLoading ? copy.loadingTikTok : noDataMessage
                        }
                        predictionStrokeDasharray={false}
                        Wrapper={Wrapper}
                        xAxisTickFormat={xAxisTickFormat}
                        xAxisTickValues={xAxisTickValues}
                        Tooltip={Tooltip}
                        country={country}
                        category={category}
                        brand={brand}
                        dataSource="instagram"
                    >
                        {showStatDisplay && (
                            <StatDisplay
                                non_cum_avg_weekly_views={
                                    metrics.non_cum_avg_weekly_views
                                }
                                week={week}
                                year={year}
                                isLoading={isLoading}
                                valueKey="non_cum_avg_weekly_likes"
                                tooltipKey="non_cum_avg_weekly_likes"
                                contentKey="dataTablePosts"
                                labelPrefix="Avg. Weekly"
                                labelSuffix="Likes"
                            />
                        )}
                        <CombinedTimeSeriesOverview
                            isLoading={isLoading}
                            metrics={
                                metrics
                                    ? [
                                          {
                                              metrics: metrics || {},
                                              metricsList,
                                              defaultMetric,
                                              week,
                                              year,
                                          },
                                      ]
                                    : null
                            }
                            showDataSource={false}
                            showDataSourceBar={showDataSourceBar}
                            customCss={customCss}
                        />
                    </Chart>
                </div>
            );
        },
    ),
    isEqual,
);

export default InstagramTimeSeries;
